/**
 * EXT-DEFERRED-PAYOUT(entry-submit) — handle the ListingPage OrderPanel submit for a
 * deferred-payout listing. Mirrors ListingPage.shared.js handleSubmit (same orderData
 * packing — kept as an extension-owned copy so the host file stays stock), but stores
 * the initial values for DeferredCheckoutPage and redirects there: the deferred
 * checkout initiates the real Sharetribe transaction (transition/request-payment-deferred)
 * and pays via empavision's PaymentElement instead of Sharetribe's Stripe integration.
 */
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { timestampToDate } from '../../../../util/dates';
import { createSlug } from '../../../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../../../util/routes';

const { UUID } = sdkTypes;

// Same packing as ListingPage.shared.js handleSubmit — booking dates/times, quantity,
// seats, delivery method, price variant.
const pickOrderDataFromFormValues = values => {
  const {
    bookingDates,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    priceVariantName, // relevant for bookings
    quantity: quantityRaw,
    seats: seatsRaw,
    deliveryMethod,
    ...otherOrderData
  } = values;

  const bookingMaybe = bookingDates
    ? {
        bookingDates: {
          bookingStart: bookingDates.startDate,
          bookingEnd: bookingDates.endDate,
        },
      }
    : bookingStartTime && bookingEndTime
    ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
    : {};
  const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
  const seats = Number.parseInt(seatsRaw, 10);
  const seatsMaybe = Number.isInteger(seats) ? { seats } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  return {
    ...bookingMaybe,
    ...priceVariantNameMaybe,
    ...quantityMaybe,
    ...seatsMaybe,
    ...deliveryMethodMaybe,
    ...otherOrderData,
  };
};

/**
 * @param {Object} parameters { params, history, routes, currentUser, callSetInitialValues, getListing }
 * @returns {Function} OrderPanel submit handler that redirects to DeferredCheckoutPage.
 */
export const handleDeferredSubmit = parameters => values => {
  const { history, params, currentUser, getListing, callSetInitialValues, routes } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const initialValues = {
    listing,
    orderData: pickOrderDataFromFormValues(values),
  };

  const saveToSessionStorage = !currentUser;

  const { setInitialValues } = findRouteByRouteName('DeferredCheckoutPage', routes);
  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

  history.push(
    createResourceLocatorString(
      'DeferredCheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};
